<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 v-if="organizationUnitId" :tid="organizationUnitId"></AdminNavigationV2>
        </div>

        <div v-if="isAccepted">
            <div class="container">
                <div class="row">
                    <div class="col-lg-7">
                        <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">
                            {{ isServiceUser ? 'Manage service account' : 'Manage user' }}
                        </h1>

                        <div v-if="!isDeleted">
                            <a :href="`/#/admin/organizations/users/manage/details/${id}`" class="btn btn-link btn-sm">
                                <i class="fa fa-pencil" aria-hidden="true"></i> Edit
                            </a>

                            <button v-if="isLocked" @click="handleUnlockUserCommandV1" class="btn btn-link btn-sm">
                                <i class="fa fa-unlock-alt" aria-hidden="true"></i> Unlock
                            </button>
                            <button v-else @click="handleLockUserCommandV1" class="btn btn-link btn-sm">
                                <i class="fa fa-lock" aria-hidden="true"></i> Lock
                            </button>

                            <!-- Password reset only for regular users, not service accounts -->
                            <div v-if="!isServiceUser" class="dropdown d-inline">
                                <button class="btn btn-link" type="button" data-toggle="dropdown" aria-expanded="false">
                                    <i class="fa fa-key" aria-hidden="true"></i> Reset password
                                </button>
                                <div class="dropdown-menu p-3 shadow-sm" style="width: 400px;">
                                    <button @click="handleResetPasswordCommandV1" class="btn btn-secondary">
                                        <i class="fa fa-key" aria-hidden="true"></i> Reset password
                                    </button>
                                    <br /><br />
                                    <div class="form-group form-check">
                                        <input v-model="sendPasswordToEmailEnabled" type="checkbox" class="form-check-input" id="sendPasswordOnEmail">
                                        <label class="form-check-label" for="sendPasswordOnEmail">Send password on additional email</label>
                                    </div>
                                    <input v-model="sendPasswordToEmail" type="email" class="form-control" placeholder="Email" v-show="sendPasswordToEmailEnabled">
                                </div>
                            </div>

                            <!-- API key management for service accounts -->
                            <a v-if="isServiceUser" :href="`/#/organizations/accesstokens/manage/${id}`" class="btn btn-link btn-sm">
                                <i class="fa fa-key" aria-hidden="true"></i> Manage API keys
                            </a>

                            <button v-if="!isServiceUser" @click="handleSignoutFromAllSessionsCommandV1" class="btn btn-link btn-sm">
                                <i class="fa fa-ban" aria-hidden="true"></i> Revoke sessions
                            </button>

                            <button @click="handleDisableUserCommandV1" class="btn btn-link btn-sm">
                                <i class="fa fa-remove" aria-hidden="true"></i> Delete
                            </button>
                        </div>

                        <div v-else>
                            <button @click="handleEnableUserCommandV1" class="btn btn-link btn-sm">
                                <i class="fa fa-undo" aria-hidden="true"></i>
                                {{ isServiceUser ? 'Restore service account' : 'Restore user' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 30px;" />

            <div class="container">
                <div v-if="!isDeleted">
                    <h3>{{displayName}}</h3>
                    <h6>{{email}}</h6>
                    <h6>
                        <span v-if="isServiceUser" class="badge badge-info" title="Service Account">Service Account</span>
                        <span v-else>{{userType}}</span>
                    </h6>

                    <!-- Informational alert for service accounts -->
                    <div v-if="isServiceUser" class="alert alert-info mt-3" role="alert">
                        <i class="fa fa-info-circle" aria-hidden="true"></i>
                        <strong>Service Account:</strong> This account is used for API access and automation. It cannot sign in through the web UI and uses API keys for authentication.
                    </div>

                    <!-- Manage roles button for all users (including service accounts) -->
                    <a :href="`/#/admin/organizations/users/manage/roles/${id}`" class="btn btn-secondary btn-sm">
                        Manage roles
                    </a>
                </div>
                <div v-else>
                    <h3 class="fg-color-red">
                        {{displayName}} is deleted
                        <span v-if="isServiceUser" class="badge badge-info ml-2">Service Account</span>
                    </h3>
                    <h6>{{email}}</h6>
                </div>
                <br />

                <!-- Workspaces section for all users (including service accounts) -->
                <div v-if="!isDeleted">
                    <h4>Workspaces</h4>
                    <h6 v-for="w in workspaces">{{w.Name}}</h6>
                    <h6 v-if="workspaces.length==0">No workspaces assigned</h6>
                    <a :href="`/#/admin/organizations/users/manage/workspaces/${id}`" class="btn btn-secondary btn-sm">
                        Manage workspaces
                    </a>
                    <p v-if="isServiceUser" class="text-muted mt-2">
                        <small><i class="fa fa-info-circle"></i> Service accounts can have workspace access for API operations.</small>
                    </p>
                </div>
            </div>
        </div>

        <div v-else-if="isRequested">
            <div class="container">
                <div class="row">
                    <div class="col-lg-7">
                        <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">Manage user access request</h1>
                    </div>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 30px;" />

            <div class="container">
                <h3>{{displayName}}</h3>
                <h6 v-if="displayName != email">{{email}}</h6>
                <h6 v-if="requestedBy">Requested by {{requestedBy}}</h6>
                <button @click="handleAcceptUserAccessRequestCommandV1" class="btn btn-lg btn-success mr-1">Accept</button>
                <button @click="handleRejectUserAccessRequestCommandV1" class="btn btn-lg btn-danger">Reject</button>
            </div>
        </div>

        <div v-else-if="isRejected">
            <div class="container">
                <div class="row">
                    <div class="col-lg-7">
                        <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">Manage user access request</h1>
                    </div>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 30px;" />

            <div class="container">
                <h3 class="text-danger">Rejected user access for {{displayName}}</h3>
                <h6 v-if="displayName != email">{{email}}</h6>
                <h6 v-if="requestedBy">Requested by {{requestedBy}}</h6>
                <button @click="handleAcceptUserAccessRequestCommandV1" class="btn btn-lg btn-success">Accept</button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, watch } from 'vue'
    import { useRoute, useRouter } from 'vue-router'

    import AdminNavigationV2 from '@common/AdminNavigationV2.vue'

    import { useCurrentUserQueryV1, useUserQueryV1 } from '@Organizations/Users/queries-vue-composables'
    import { useWorkspacesByUserQueryV1 } from '@Organizations/Workspaces/queries-vue-composables'
    
    import
        {
            useLockUserCommandV1,
            useUnlockUserCommandV1,
            useResetPasswordCommandV1,
            useSignoutFromAllSessionsCommandV1,
            useDisableUserCommandV1,
            useEnableUserCommandV1,
            useAcceptUserAccessRequestCommandV1,
            useRejectUserAccessRequestCommandV1
        } from '@Organizations/Users/commands-vue-composables'

    // ---------------- router ----------------

    const route = useRoute()
    const router = useRouter()

    const id = computed(() => String(route.params.id))

    // ---------------- local state ----------------

    const sendPasswordToEmailEnabled = ref(false)
    const sendPasswordToEmail = ref<string | null>(null)

    // ---------------- queries ----------------

    const { runCurrentUserQueryV1,
        authorized }
        = useCurrentUserQueryV1(false, true, false)

    const {
        runUserQueryV1,
        modelUserQueryV1: user
    } = useUserQueryV1(id)

    const {
        runWorkspacesByUserQueryV1,
        modelWorkspacesByUserQueryV1: userWorkspaces
    } = useWorkspacesByUserQueryV1(id)

    // ---------------- derived ----------------

    const email = computed(() => user.value?.Email)
    const displayName = computed(() => user.value?.DisplayName)
    const isServiceUser = computed(() => user.value?.IsServiceUser ?? false)
    const userType = computed(() => user.value?.IsGlobalAdmin ? 'Global Admin' : 'User')

    const isLocked = computed(() => user.value?.IsLocked)
    const isDeleted = computed(() => user.value?.IsDeleted)
    const isRequested = computed(() => user.value?.IsRequested)
    const isAccepted = computed(() => user.value?.IsAccepted)
    const isRejected = computed(() => user.value?.IsRejected)

    const requestedBy = computed(() => user.value?.RequestedBy)
    const organizationUnitId = computed(() => user.value?.OrganizationUnitId)

    const workspaces = computed(() => userWorkspaces.value?.Workspaces ?? [])

    // ---------------- watch local flags ----------------

    watch(sendPasswordToEmailEnabled, enabled =>
    {
        if (!enabled)
        {
            sendPasswordToEmail.value = null
        }
    })

    // ---------------- commands ----------------

    const { invokeLockUserCommandV1, succeedLockUserCommandV1, errorLockUserCommandV1 } = useLockUserCommandV1(id)
    const { invokeUnlockUserCommandV1, succeedUnlockUserCommandV1, errorUnlockUserCommandV1 } = useUnlockUserCommandV1(id)
    const { invokeResetPasswordCommandV1, succeedResetPasswordCommandV1, errorResetPasswordCommandV1 } = useResetPasswordCommandV1(id, sendPasswordToEmail)
    const { invokeSignoutFromAllSessionsCommandV1, succeedSignoutFromAllSessionsCommandV1, errorSignoutFromAllSessionsCommandV1 } = useSignoutFromAllSessionsCommandV1(id)
    const { invokeDisableUserCommandV1, succeedDisableUserCommandV1, errorDisableUserCommandV1 } = useDisableUserCommandV1(id)
    const { invokeEnableUserCommandV1, succeedEnableUserCommandV1, errorEnableUserCommandV1 } = useEnableUserCommandV1(id)
    const { invokeAcceptUserAccessRequestCommandV1, succeedAcceptUserAccessRequestCommandV1, errorAcceptUserAccessRequestCommandV1 } = useAcceptUserAccessRequestCommandV1(id)
    const { invokeRejectUserAccessRequestCommandV1, succeedRejectUserAccessRequestCommandV1, errorRejectUserAccessRequestCommandV1 } = useRejectUserAccessRequestCommandV1(id)

    // ---------------- lifecycle ----------------

    onMounted(() =>
    {
        document.title = 'Manage user'
        runCurrentUserQueryV1()
        runUserQueryV1()
        runWorkspacesByUserQueryV1()
    })

    watch(succeedAcceptUserAccessRequestCommandV1, (succeeded) => {
        if (succeeded) {
             router.push(`/admin/organizations/users/requests/${organizationUnitId.value}`)
        }
    })

    watch(succeedRejectUserAccessRequestCommandV1, (succeeded) => {
        if (succeeded) {
             router.push(`/admin/organizations/users/requests/${organizationUnitId.value}`)
        }
    })

    //watch(authorized, () =>
    //{
    //    if (authorized.value === false)
    //    {
    //        location.href = '/#/signin';
    //    }
    //})

    watch(
    [
        succeedLockUserCommandV1,
        succeedUnlockUserCommandV1,
        succeedDisableUserCommandV1,
        succeedEnableUserCommandV1,
        succeedResetPasswordCommandV1,
        succeedSignoutFromAllSessionsCommandV1
    ],
    (states) => {
        if (states.some(Boolean)) {
            runUserQueryV1()
        }
    })

    watch(
    [
        errorLockUserCommandV1,
        errorUnlockUserCommandV1,
        errorDisableUserCommandV1,
        errorEnableUserCommandV1,
        errorResetPasswordCommandV1,
        errorSignoutFromAllSessionsCommandV1,
        errorRejectUserAccessRequestCommandV1,
        errorAcceptUserAccessRequestCommandV1
    ],
    (errors) => {
        const error = errors.find(e => !!e)

        if (error) {
            alert(error);
        }
    })

    function handleLockUserCommandV1()
    {
        const type = isServiceUser.value ? 'service account' : 'user'
        if (window.confirm(`Do you really want to lock ${type} ${email.value}?`))
        {
            invokeLockUserCommandV1()
        }
    }

    function handleUnlockUserCommandV1()
    {
        const type = isServiceUser.value ? 'service account' : 'user'
        if (window.confirm(`Do you really want to unlock ${type} ${email.value}?`))
        {
            invokeUnlockUserCommandV1()
        }
    }

    function handleResetPasswordCommandV1()
    {
        if (window.confirm(`Do you really want to reset password for user ${email.value}?`))
        {
            invokeResetPasswordCommandV1()
        }
    }

    function handleSignoutFromAllSessionsCommandV1()
    {
        if (window.confirm(`Do you really want to revoke all sessions for user ${email.value}?`))
        {
            invokeSignoutFromAllSessionsCommandV1()
        }
    }

    function handleDisableUserCommandV1()
    {
        const type = isServiceUser.value ? 'service account' : 'user'
        if (window.confirm(`Do you really want to delete ${type} ${email.value}?`))
        {
            invokeDisableUserCommandV1()
        }
    }

    function handleEnableUserCommandV1()
    {
        const type = isServiceUser.value ? 'service account' : 'user'
        if (window.confirm(`Do you really want to restore ${type} ${email.value}?`))
        {
            invokeEnableUserCommandV1()
        }
    }

    function handleAcceptUserAccessRequestCommandV1()
    {
        if (window.confirm(`Do you really want to accept user access request for ${email.value}?`))
        {
            invokeAcceptUserAccessRequestCommandV1()
        }
    }

    function handleRejectUserAccessRequestCommandV1()
    {
        if (window.confirm(`Do you really want to reject user access request for ${email.value}?`))
        {
            invokeRejectUserAccessRequestCommandV1()
        }
    }
</script>

